import { debounce } from '@mui/material';
import {
  DataGridPremium,
  GRID_AGGREGATION_FUNCTIONS,
  isAutogeneratedRow,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
} from '@mui/x-data-grid-premium';
import { useCallback, useMemo, useState } from 'react';

import { daDK } from '@mui/x-data-grid/locales';

import { useTranslation } from 'react-i18next';
import CustomNoRowsOverlay from '../../../../components/datagrid/DataGridNoRowsOverlay';
import { useDashboardColumns } from '../../hooks/dashboard-columns';
import PortfolioAnalysisToolbar from '@/features/portfolio-analysis/components/datagrid/PortfolioAnalysisToolbar';
import { useLocalStorageJson } from '@/hooks/useLocalStorageJson';
import { isChildRow } from '@/features/portfolio-analysis/utils/datagrid-helpers';

const firstAlphabeticalAggregation = {
  // The `apply` method takes the values to aggregate and returns the aggregated value
  apply: (params) => {
    if (params.values.length === 0) {
      return params.values[0];
    }

    const sortedValue = params.values.sort((a = '', b = '') =>
      a.localeCompare(b),
    );

    return sortedValue[0];
  },
  // The `label` property defines the label displayed in the column header when this aggregation is being used.
  label: 'firstAlphabetical',
  // The `types` property defines which type of columns can use this aggregation function.
  // Here, we only want to propose this aggregation function for `string` columns.
  // If not defined, aggregation will be available for all column types.
  columnTypes: ['string'],
};

const PortfolioAnalysisDashboardDatagrid = ({
  data,
  setSelectedMeter,
  setMeterType,
  isLoading,
}) => {
  const { t } = useTranslation('portfolio-analysis_dashboard');

  const [favorites, setFavorites] = useLocalStorageJson(
    'portfolio_analysis_favorites',
    {},
  );

  // 1️⃣ derive instead of storing
  const transformedData = useMemo(() => {
    return {
      ...data,
      portfolio_analysis_list: data.portfolio_analysis_list.map(
        (item, index) => ({
          ...item,
          id: index,
          average_weekday_night: item.average_weekday_night ?? null,
          inactive_active_ratio: item.inactive_active_ratio ?? null,
          favorites: Boolean(favorites[item.metering_point_id]),
          meter_in_sync: item.meter_in_sync ?? true,
        }),
      ),
    };
  }, [data, favorites]);

  const preset = useMemo(
    () => ({
      order: [
        'favorites',
        'type',
        '__row_group_by_columns_group__',
        'ExtractedBuildingUse',
        'SumBuildingArea',
        'occupancy',
        'daytime_start',
        'daytime_end',
        'power_total_consumption_year',
        'water_total_consumption_year',
        'heat_total_consumption_year',
        'warnings',
      ],
      sortingModel: [{ field: '__row_group_by_columns_group__', sort: 'desc' }],
      rowGrouping: {
        model: ['matrikel_key'],
      },
      filterModel: {
        items: [],
      },
      columnGroupingModel: [
        {
          groupId: 'Ejendomsdata',
          headerName: t('columnGroups.propertyData.header'),
          description: t('columnGroups.propertyData.description'),
          children: [
            { field: 'ExtractedBuildingUse' },

            { field: 'AverageBuildingYear' },
            { field: 'SumBuildingArea' },
          ],
        },
        {
          groupId: 'Kategorisering',
          headerName: t('columnGroups.categorisation.header'),
          description: t('columnGroups.categorisation.description'),
          children: [
            { field: 'ExtractedHeatInstallation' },
            { field: 'EnergyLabelClassification' },
          ],
        },
        {
          groupId: 'yearly_total',
          description: t('columnGroups.yearlyTotal.description'),
          headerName: t('columnGroups.yearlyTotal.header'),
          children: [
            { field: 'power_total_consumption_year' },
            { field: 'water_total_consumption_year' },
            { field: 'heat_total_consumption_year' },
          ],
        },
        {
          groupId: 'Aktivitetsmønstre',
          headerName: t('columnGroups.activityPatterns.header'),
          description: t('columnGroups.activityPatterns.description'),
          children: [
            { field: 'occupancy' },
            { field: 'daytime_start' },
            { field: 'daytime_end' },
          ],
        },
      ],
    }),
    [t],
  );

  const apiRef = useGridApiRef();

  const totalColumns = useDashboardColumns(transformedData, preset);

  const [isFavoritesFiltered, setIsFavoritesFiltered] = useState(false);

  const [datagridState, setDatagridState] = useState({});

  function generateVisibilityModel(order, totalColumns) {
    // Initialize visibility model with all columns set to false
    let columnVisibilityModel = {};
    totalColumns.forEach((column) => {
      if (column.field != '__row_group_by_columns_group__')
        columnVisibilityModel[column.field] = false;
    });

    // Set the columns in order array to true
    order.forEach((column) => {
      if (Object.prototype.hasOwnProperty.call(columnVisibilityModel, column)) {
        columnVisibilityModel[column] = true;
      }
    });
    return columnVisibilityModel;
  }

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      columns: {
        columnVisibilityModel: {
          ...generateVisibilityModel(preset.order, totalColumns),
        },
      },
      pinnedColumns: {
        left: [
          'favorites',
          'type',
          'address',
          '__row_group_by_columns_group__',
        ],
      },
      sorting: {
        sortModel: [...preset.sortingModel],
      },
      rowGrouping: {
        model: [...preset.rowGrouping.model],
      },
      filter: {
        filterModel: {
          items: [...preset.filterModel.items],
          quickFilterExcludeHiddenColumns: false,
        },
      },
      aggregation: {
        model: {},
      },
      density: 'compact',
    },
  });

  const handleCellClick = useCallback(
    (params) => {
      if (params.field !== 'favorites') {
        if (typeof params.id === 'string' || params.id instanceof String) {
          setMeterType('matrikel');
          const firstChildId = params.rowNode.children[0];
          const firstChild = apiRef.current.getRow(firstChildId);
          setSelectedMeter(firstChild);
        } else {
          const row = apiRef.current.getRow(params.id);
          const isLeaf =
            transformedData.portfolio_analysis_list.filter(
              (o) => o.matrikel_key == row.matrikel_key,
            ).length == 1;
          if (isLeaf) {
            setMeterType('matrikelMeter');
          } else {
            setMeterType('meter');
          }
          setSelectedMeter(params.row);
        }
        return;
      }

      if (params.field !== 'favorites') return;

      if (params.rowNode?.children?.length) {
        /* -------- group row -------- */
        setFavorites((prev) => {
          // 1️⃣ compute with the *current* state
          const allFav = params.rowNode.children.every((id) => {
            const child = apiRef.current.getRow(id);
            return child && prev[child.metering_point_id];
          });

          // 2️⃣ build the next object
          const next = { ...prev };
          params.rowNode.children.forEach((id) => {
            const child = apiRef.current.getRow(id);
            if (child) next[child.metering_point_id] = !allFav;
          });
          return next;
        });
      } else {
        /* -------- single row -------- */
        setFavorites((prev) => ({
          ...prev,
          [params.row.metering_point_id]: !prev[params.row.metering_point_id],
        }));
      }
    },
    [
      apiRef,
      setFavorites,
      setMeterType,
      setSelectedMeter,
      transformedData.portfolio_analysis_list,
    ],
  );

  const handleFavoritesFilterClick = (bool) => {
    if (bool) {
      setFilterModel((prev) => {
        return {
          items: [
            ...prev.items,
            {
              field: 'favorites',
              operator: 'is',
              value: true,
            },
          ],
        };
      });
    }
    if (!bool) {
      setFilterModel((prev) => {
        return {
          items: prev.items.filter((value) => value.field !== 'favorites'),
        };
      });
    }
  };
  const [expandedGroups, setExpandedGroups] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const HandleNodeClick_withSavedExpandState = useCallback(
    debounce((node) => {
      // Get current apiRef state
      const CurrentGridState = apiRef.current.state;
      // Dynamically identify the group headings that are currently visible by using the visibleRowsLookup object, and the fact that group headings start with "auto-generated-row-type/"
      const visibleGroups = Object.keys(
        CurrentGridState.visibleRowsLookup,
      ).filter((row) => row.startsWith('auto-generated-row-matrikel_key/'));

      // Store the identified group headings for reference
      const visibleGroupsWithoutPrefix = visibleGroups.map((row) =>
        row.replace('auto-generated-row-matrikel_key/', ''),
      );

      // To check if each group is currently expanded, check if the first row of each group is visible
      const firstRowOfEachGroup = visibleGroups.map(
        (groupId) => apiRef.current.getRowGroupChildren({ groupId })[0],
      );
      const firstRowVisibility = firstRowOfEachGroup.map(
        (row) => CurrentGridState.visibleRowsLookup[row],
      );

      // Build an array of the visible groups (without prefix), based on those for which which the first row is visible
      const visibleExpandedGroups = visibleGroupsWithoutPrefix.filter(
        (_, index) => firstRowVisibility[index],
      );

      // Pass this array of expanded groups to the state for the next render
      setExpandedGroups(visibleExpandedGroups);

      // Call the original trigger for a state update
      handleCellClick(node);
    }, 100),
    [transformedData],
  );

  const expandAllGroups = useCallback(() => {
    const CurrentGridState = apiRef.current.state;

    // Get all visible group row IDs
    const visibleGroups = Object.keys(
      CurrentGridState.visibleRowsLookup,
    ).filter((row) => row.startsWith('auto-generated-row-matrikel_key/'));

    // Set each group to expanded
    visibleGroups.forEach((groupId) => {
      apiRef.current.setRowChildrenExpansion(groupId, true);
    });

    // Optionally update your saved expandedGroups state
    const visibleGroupsWithoutPrefix = visibleGroups.map((row) =>
      row.replace('auto-generated-row-matrikel_key/', ''),
    );
    setExpandedGroups(visibleGroupsWithoutPrefix);
  }, [apiRef, setExpandedGroups]);
  const handleExpandCollapse = () => {
    if (expandedGroups.length === 0) {
      expandAllGroups();
    } else {
      setExpandedGroups([]);
    }
  };
  const isGroupExpandedByDefault = useCallback(
    (node) =>
      node.groupingField === 'matrikel_key' &&
      expandedGroups.includes(String(node.groupingKey)),
    [expandedGroups],
  );
  const [filterModel, setFilterModel] = useState(preset.filterModel);
  const [rowGroupingModel, setRowGroupingModel] = useState(
    preset.rowGrouping.model,
  );
  return (
    <>
      <DataGridPremium
        localeText={daDK.components.MuiDataGrid.defaultProps.localeText}
        slots={{
          toolbar: PortfolioAnalysisToolbar,
          noRowsOverlay: () => <CustomNoRowsOverlay rows={t('noRows')} />,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            handleFavoritesFilterClick: handleFavoritesFilterClick,
            isFavoritesFiltered: isFavoritesFiltered,
            setIsFavoritesFiltered: setIsFavoritesFiltered,
            handleExpandCollapse: () => {
              handleExpandCollapse();
            },
            isCollapsed: expandedGroups.length === 0,
            filterModel: filterModel,
          },
          loadingOverlay: {
            variant: 'linear-progress',
            noRowsVariant: 'skeleton',
          },
        }}
        loading={isLoading}
        rows={transformedData.portfolio_analysis_list}
        columns={totalColumns}
        showCellVerticalBorder={false}
        showColumnVerticalBorder={true}
        pageSize={5}
        columnGroupingModel={preset.columnGroupingModel}
        apiRef={apiRef}
        aggregationFunctions={{
          ...GRID_AGGREGATION_FUNCTIONS,
          firstAlphabetical: firstAlphabeticalAggregation,
        }}
        getAggregationPosition={() => 'inline'}
        isGroupExpandedByDefault={isGroupExpandedByDefault}
        rowGroupingModel={rowGroupingModel}
        onRowGroupingModelChange={(newModel) => setRowGroupingModel(newModel)}
        filterModel={filterModel}
        onFilterModelChange={(newModel) => setFilterModel(newModel)}
        initialState={initialState}
        onCellClick={HandleNodeClick_withSavedExpandState}
        //columnHeaderHeight={100}
        sortingOrder={['desc', 'asc', null]}
        getRowClassName={(params) =>
          params.row.type_of_mp === 'PV' ? 'solar-row' : ''
        }
        getCellClassName={(params) => {
          if (
            isAutogeneratedRow(params.row) &&
            params.field === '__row_group_by_columns_group__'
          ) {
            return 'autogenerated';
          }
          if (
            !isAutogeneratedRow(params.row) &&
            isChildRow(params) &&
            params.field === '__row_group_by_columns_group__'
          ) {
            return 'left-extra-margin-cell';
          }
          if (
            !isAutogeneratedRow(params.row) &&
            !isChildRow(params) &&
            params.field === '__row_group_by_columns_group__'
          ) {
            return 'left-margin-cell';
          }
          return '';
        }}
        onColumnVisibilityModelChange={(newModel) => {
          setDatagridState({
            ...datagridState,
            columnVisibilityModel: newModel,
          });
        }}
        groupingColDef={{
          width: 350,
          leafField: 'address',

          valueFormatter: (value) => {
            const list = transformedData.portfolio_analysis_list.filter(
              (item) => {
                return item.matrikel_key === value;
              },
            );
            return list.length > 0 ? list[0].main_address : value;
          },
          sortComparator: (date1, date2, cellParams1, cellParams2) => {
            let value1 = date1;
            let value2 = date2;

            let list1 = transformedData.portfolio_analysis_list.filter(
              (item) => item.matrikel_key === date1,
            );
            let list2 = transformedData.portfolio_analysis_list.filter(
              (item) => item.matrikel_key === date2,
            );
            if (list1.length > 0) value1 = list1[0].main_address;
            if (list2.length > 0) value2 = list2[0].main_address;

            if (
              cellParams1.rowNode.type !== 'group' ||
              cellParams2.rowNode.type !== 'group'
            ) {
              const api = cellParams1.api;
              const comparableDate1 =
                value1 ||
                (cellParams1.rowNode.children
                  ? api.getRow(cellParams1.rowNode.children[0])?.main_address
                  : undefined);
              const comparableDate2 =
                value2 ||
                (cellParams2.rowNode.children
                  ? api.getRow(cellParams2.rowNode.children[0])?.main_address
                  : undefined);
              if (!comparableDate1 || !comparableDate2) {
                return 0;
              }
              // Ascending order: return 1 if 1 > 2, -1 if 1 < 2
              return comparableDate1 > comparableDate2 ? -1 : 1;
            }
            // For group rows, compare directly
            return value1 > value2 ? -1 : 1;
          },
        }}
        sx={{
          '.MuiDataGrid-aggregationColumnHeaderLabel': {
            display: 'none',
          },
          '& .cell-left-border': {
            borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
          },
          '& .left-margin-cell': {
            paddingLeft: 7,
          },
          '& .autogenerated': {
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-columnHeaderTitleContainerContent': {
            height: '100%',
          },
          '&.MuiDataGrid-root .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer':
            {
              pl: 1,
            },
          border: 'none',
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
            {
              outline: 'none',
            },
          '& .MuiDataGrid-root': {
            border: 0,
          },
          '& .MuiDataGrid-footerContainer': {
            border: 0,
          },
          '& .MuiDataGrid-bottomContainer': {
            border: 0,
          },
          '& .MuiDataGrid-main': {
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? '#fff' : '#121212',
            borderRadius: 2,
            border: (theme) => `1px solid ${theme.palette.divider}`,
          },
          '& .selected-row': {
            backgroundColor: (theme) => theme.palette.action.selected,
            '&:hover': {
              backgroundColor: (theme) => theme.palette.action.selected,
            },
          },
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
          '& .solar-row .MuiDataGrid-cell': {
            color: '#00c853', // MUI green[600]
          },
        }}
      />
    </>
  );
};

export default PortfolioAnalysisDashboardDatagrid;
